// #region Imports

import type { CssColor } from './CssColor';

// #endregion

/**
 * Represents the shade names available in a theme palette.
 *
 * @public
 */
export type ThemeShadeName = '0' | '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';

/**
 * Represents a dynamic theme palette.
 *
 * @public
 */
export type ThemePalette = Partial<Record<ThemeShadeName, CssColor>>;

/**
 * @public
 */
export namespace ThemePalette {

    const THEME_SHADE_NAMES: ReadonlyArray<ThemeShadeName> = ['0', '50', '100', '200', '300', '400', '500', '600', '700', '800', '900'];

    /**
     * Checks if the value is a theme shade name.
     *
     * @private
     * @param value - The value to check.
     * @returns The result.
     */
    function isThemeShadeName(value: string): value is ThemeShadeName {
        return THEME_SHADE_NAMES.includes(value as ThemeShadeName);
    }

    /**
     * Checks if the value is a theme palette.
     *
     * @public
     * @param value - The value to check.
     * @returns The result.
     */
    export function isThemePalette(value: unknown): value is ThemePalette {
        if (typeof value !== 'object' || value === null) {
            return false;
        }

        return Object.keys(value).every((key) => isThemeShadeName(key) && typeof (value as Record<string, unknown>)[key] === 'string');
    }

}
